import React, { Suspense, lazy } from 'react';
import { Box, CircularProgress, useTheme, useMediaQuery } from '@mui/material';
import LandingSection from '../../shared/components/landing-section';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';

// Lazy load components for better performance
const ClientAboutUs = lazy(() => import('./client/about-us'));
const ClientTalentSection = lazy(() => import('./client-talent-section'));
const WhatWeDo = lazy(() => import('./client/what-we-do'));
const Proccess = lazy(() => import('./proccess'));

// Loading component
const LoadingSpinner: React.FC = () => (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
    >
        <CircularProgress color="primary" />
    </Box>
);

// Error boundary component
class ErrorBoundary extends React.Component<
    { children: React.ReactNode; fallback?: React.ReactNode },
    { hasError: boolean }
> {
    constructor(props: { children: React.ReactNode; fallback?: React.ReactNode }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): { hasError: boolean } {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('Landing section error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || (
                <Box 
                    display="flex" 
                    justifyContent="center" 
                    alignItems="center" 
                    minHeight="200px"
                    color="text.secondary"
                >
                    Something went wrong. Please refresh the page.
                </Box>
            );
        }

        return this.props.children;
    }
}

// Section wrapper with error boundary and suspense
const SectionWrapper: React.FC<{ 
    children: React.ReactNode; 
    id: string;
    backgroundColor?: string;
    minHeight?: string;
}> = ({ children, id, backgroundColor, minHeight = "auto" }) => (
    <LandingSection
        id={id}
        style={{
            width: '100%',
            backgroundColor: backgroundColor || 'transparent',
            minHeight,
            transition: 'all 0.3s ease-in-out'
        }}
    >
        <ErrorBoundary>
            <Suspense fallback={<LoadingSpinner />}>
                {children}
            </Suspense>
        </ErrorBoundary>
    </LandingSection>
);

const LandingHomeLayout: React.FC = () => {
    const { t } = useTranslation();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSpanish = t('language') === 'Idioma'; // Simple check for Spanish

    // Adjust heights for Spanish content which tends to be longer
    const getMinHeight = (baseHeight: string, mobileHeight: string) => {
        if (isMobile) {
            return isSpanish ? mobileHeight : mobileHeight;
        }
        return baseHeight;
    };

    // Section configurations with responsive heights
    const sections = [
        {
            id: "aboutUs",
            backgroundColor: theme.palette.primary.main,
            component: <ClientAboutUs />,
            minHeight: getMinHeight("70vh", "60vh")
        },
        {
            id: t("nav.services"),
            backgroundColor: '#ffffff',
            component: <WhatWeDo />,
            minHeight: getMinHeight("auto", "auto")
        },
        {
            id: "clientTalent",
            backgroundColor: 'transparent',
            component: <ClientTalentSection />,
            minHeight: getMinHeight("auto", "auto")
        },
        {
            id: t("nav.steps"),
            backgroundColor: "#fbf9e8",
            component: <Proccess />,
            minHeight: getMinHeight("auto", "auto")
        }
    ];

    return (
        <Box 
            component="main"
            sx={{
                // Ensure proper layout flow without fixed positioning issues
                width: '100%',
                '& > div': {
                    scrollMarginTop: '80px', // Account for fixed header
                },
                // Smooth scrolling for better UX
                scrollBehavior: 'smooth'
            }}
        >
            {sections.map((section, index) => (
                <SectionWrapper
                    key={`${section.id}-${index}`}
                    id={section.id}
                    backgroundColor={section.backgroundColor}
                    minHeight={section.minHeight}
                >
                    {section.component}
                </SectionWrapper>
            ))}

            {/* Future sections can be easily added here */}
            {/* Uncommented section for future use
            <SectionWrapper
                id={t("nav.about")}
                backgroundColor="transparent"
            >
                <LandingAbout />
            </SectionWrapper>
            */}
        </Box>
    );
};

export default React.memo(LandingHomeLayout);

